'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { Select, SelectProps } from '../select';

export interface FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<SelectProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * Select component integrated with React Hook Form
 * Handles controlled state automatically
 */
export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormSelectProps<TFieldValues, TName>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <Select
      {...props}
      ref={ref}
      value={value || ''}
      onChange={onChange}
      error={showError ? error?.message : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
    />
  );
}

FormSelect.displayName = 'FormSelect';
